// Pure helpers for the mask UI in ClassicLayout / MaskPanel.
//
// Phase 6.7 R12 (consult 2026-05-05): MASK UI を旧 Java applet `MaskSetCanvas`
// 風の **2 slot + 下部 type toggle** に全面置換 (`classic-ui-restore.md` §4 R12).
// 旧 16 色 swatch + 3 値ラジオは廃止.
//
// 新セマンティクス:
//   - slot1 / slot2 は **`number | null`** (= 旧 Java の `value=-1` の意味). null は
//     未設定 = ✕ 表示, 数値はその色で塗りつぶし表示.
//   - `pen.mask.colors` は protocol 不変の number[] (max 2). slot1/slot2 から
//     non-null だけ集めて並べ替える.
//   - `pen.mask.type` は protocol の `'none' | 'normal' | 'inverse'` のうち,
//     UI 内部 state は `'normal' | 'inverse'` のみ (旧版踏襲, `'none'` の明示
//     選択は廃止). 両 slot 未設定なら自動的に `'none'` に倒す変換層を入れる.
//
// `toggleMaskColor` は MaskPanel の旧実装で使っていたが, 新 UI (slot ベース) では
// 不要. 削除すると ClassicLayout / MaskPanel から参照が消えるため一旦残す.
//
// (architecture.md §16 / classic-ui-restore.md §4 R12 / protocol-events.md §1
//  — `mask.colors.max(2)` constraint)

import type { StrokeMask } from '@oekaki/protocol';

/**
 * Toggle a color in the mask color list, with the 2-slot cap. (旧実装互換, R12 で
 * 直接 UI からは呼ばれなくなったが過去テスト互換のため残置.)
 *
 * If the color is already present it gets removed; otherwise it is appended
 * unless we already hold 2 colors, in which case the oldest entry is dropped
 * (FIFO). The FIFO eviction is the legacy Java applet behaviour.
 */
export const toggleMaskColor = (current: ReadonlyArray<number>, color: number): number[] => {
  const idx = current.indexOf(color);
  if (idx >= 0) {
    return current.filter((_, i) => i !== idx);
  }
  if (current.length >= 2) {
    return [...current.slice(1), color];
  }
  return [...current, color];
};

/** Slot 値. `null` = 旧 Java applet の `value=-1` (未設定 = ✕ 表示). */
export type MaskSlotValue = number | null;

/** UI が保持する type (`'none'` は廃止 — 両 slot null から派生する). */
export type MaskTypeUI = 'normal' | 'inverse';

/**
 * 2 slot + type → wire-level `StrokeMask` への変換.
 *
 * 旧版踏襲のセマンティクス:
 *   - 両 slot とも null (未設定) → `type='none'`, `colors=[]` (mask 無効化)
 *   - 1 つ以上設定済 → `type=type` (normal/inverse), `colors=non-null だけ並べた配列`
 *
 * @param slot1 左 slot の色 (null=未設定).
 * @param slot2 右 slot の色 (null=未設定).
 * @param type 下部 toggle で保持されている UI 値 ('normal' | 'inverse').
 */
export const slotsToMask = (
  slot1: MaskSlotValue,
  slot2: MaskSlotValue,
  type: MaskTypeUI,
): StrokeMask => {
  const colors: number[] = [];
  if (slot1 !== null) colors.push(slot1);
  if (slot2 !== null) colors.push(slot2);
  if (colors.length === 0) {
    return { type: 'none', colors: [] };
  }
  return { type, colors };
};
